<template>
  <section>
    <div class="order-detail">
      <b-loading :active="loading" />
      <h2 class="order-detail__title">
        {{ title }}
      </h2>
      <div class="columns order-detail__content">
        <div class="column">
          <div class="detail-card detail-card--info">
            <h3 class="detail-card__title">
              店家資訊
              <w-button
                v-if="!viewMode && isAllowToEdit"
                class="detail-card__link"
                type="text"
                @click.prevent="prevStep"
              >
                重選店家
              </w-button>
            </h3>
            <div class="columns is-flex-wrap-wrap">
              <div class="column is-half">
                <b-field
                  horizontal
                  label="店家名稱">
                  {{ menu.storeInfo.store_detail.name }}
                </b-field>
              </div>
              <div v-if="storePhone" class="column is-half">
                <b-field horizontal label="電話訂購">
                  {{ storePhone }}
                </b-field>
              </div>
              <div v-if="storeClosedDays" class="column is-half">
                <b-field horizontal label="公休日">
                  {{ storeClosedDays }}
                </b-field>
              </div>
              <div v-if="storeOrderMethods.length" class="column is-half">
                <b-field horizontal class="actionable">
                  <template #label>
                    <label>線上訂購</label>
                  </template>
                  <div class="flex gap-x-2 items-center">
                    <div class="flex gap-x-4 items-center">
                      <img
                        v-for="method in storeOrderMethods"
                        :key="`image-${method.key}`"
                        :alt="`${method.title}圖示`"
                        :src="getOrderMethodImage(method.icon)"
                      >
                    </div>
                    <b-tooltip
                      :auto-close="['outside', 'escape']"
                      :triggers="['click']"
                      class="cursor-pointer"
                      multilined
                      position="is-bottom"
                      size="is-small"
                      type="is-light"
                    >
                      <i class="icon-drop-down ml-1 text-gray-500" />
                      <template v-slot:content>
                        <div
                          v-for="method in storeOrderMethods"
                          :key="`method-${method.key}`"
                          class="tooltip-content__order-method"
                        >
                          <img
                            :alt="`${method.title}圖示`"
                            :key="`image-${method.key}`"
                            :src="getOrderMethodImage(method.icon)"
                            class="min-w-[24px]"
                          >
                          <a
                            v-if="method.linkTitle"
                            :href="menu.storeInfo.store_order_way[method.key]"
                            target="_blank"
                          >
                            {{ method.linkTitle }}
                          </a>
                          <p
                            v-else
                            :title="menu.storeInfo.store_order_way[method.key]"
                          >
                            {{ menu.storeInfo.store_order_way[method.key] }}
                          </p>
                        </div>
                      </template>
                    </b-tooltip>
                  </div>
                </b-field>
              </div>
              <div v-if="storePaymentMethods" class="column is-half">
                <b-field horizontal label="收款方式">
                  {{ storePaymentMethods }}
                </b-field>
              </div>
            </div>
          </div>
          <div class="detail-card detail-card--info subsidy-info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                補助額度
              </h3>
              <span class="text-gray-500 text-sm">
                系統將依據補助額度計算人員自付額
              </span>
            </div>
            <div>
              <b-field
                horizontal
                label="每人補助金額"
                :type="{
                  'is-danger': v$.orderInfo.subsidy.$invalid
                }"
                :message="v$.orderInfo.subsidy?.$silentErrors[0]?.$message">
                <b-input
                  v-model.number="orderInfo.subsidy"
                  :disabled="viewMode"
                  class="w-32"
                  placeholder="$0"
                  @keypress.native="filterNonPositiveNumber"
                />
              </b-field>
            </div>
          </div>
          <div class="detail-card detail-card--info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                消費額度
              </h3>
              <span class="text-gray-500 text-sm">
                設定消費額度後，僅能下單額度內的訂購。
              </span>
            </div>
            <div>
              <b-field v-if="showLimitOption(consumptionLimitList.NO_LIMIT)">
                <b-radio
                  :disabled="viewMode"
                  :native-value="consumptionLimitList.NO_LIMIT"
                  v-model="orderInfo.limit"
                >
                  <span>不限制</span>
                </b-radio>
              </b-field>
              <div
                v-if="showLimitOption(consumptionLimitList.AMOUNT)"
                class="flex items-center mb-3 sm:flex-col sm:items-start"
              >
                <b-field class="mb-0 mr-2">
                  <b-radio
                    :disabled="viewMode"
                    :native-value="consumptionLimitList.AMOUNT"
                    v-model="orderInfo.limit"
                  >
                    <span>每人限制總消費金額</span>
                  </b-radio>
                </b-field>
                <b-field class="w-20 limit-input">
                  <b-input
                    v-model.number="limitAmount"
                    :disabled="orderInfo.limit !== consumptionLimitList.AMOUNT || viewMode"
                    placeholder="$0"
                    @keypress.native="filterNonPositiveNumber"
                  />
                </b-field>
              </div>
              <div
                v-if="showLimitOption(consumptionLimitList.ITEMS)"
                class="flex items-center mb-3 sm:flex-col sm:items-start"
              >
                <b-field class="mb-0 mr-2">
                  <b-radio
                    :disabled="viewMode"
                    :native-value="consumptionLimitList.ITEMS"
                    v-model="orderInfo.limit"
                  >
                    <span>每人限制總訂購項目數</span>
                  </b-radio>
                </b-field>
                <b-field class="w-20 limit-input">
                  <b-input
                    v-model.number="limitItems"
                    :disabled="orderInfo.limit !== consumptionLimitList.ITEMS || viewMode"
                    placeholder="0"
                    @keypress.native="filterNonPositiveNumber"
                  />
                </b-field>
              </div>
            </div>
          </div>
          <div class="detail-card detail-card--info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                品項備註設定
              </h3>
              <span class="text-gray-500 text-sm">
                開放使用者可於訂單內輸入品項備註。
              </span>
            </div>
            <b-field horizontal label="開放品項備註">
              <b-switch
                v-model="orderInfo.enableOrderNote"
                :disabled="viewMode"
                class="mx-2"
                size="is-small"
              />
            </b-field>
          </div>
          <div class="detail-card detail-card--info">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="detail-card__title mr-4">設定品項庫存量</h3>
              <span class="text-gray-500 text-sm">
                可針對不同品項設定庫存量以限制訂購總數，留空表示不限制。
              </span>
            </div>
            <label class="font-medium text-xl">
              {{ menu?.name }}
            </label>
            <b-table :data="menuItems" :mobile-cards="false" class="mt-4" sticky-header>
              <b-table-column
                header-class="text-xs"
                field="index"
                label="No."
                v-slot="props"
                width="28"
              >
                {{ props.index + 1 }}.
              </b-table-column>
              <b-table-column
                field="name"
                header-class="text-xs"
                cell-class="min-w-[150px]"
                label="品項名稱"
                v-slot="props"
              >
                <b-tooltip
                  :label="props.row.name"
                  position="is-bottom"
                  type="is-dark"
                >
                  {{ props.row.name }}
                </b-tooltip>
              </b-table-column>
              <b-table-column
                field="category"
                header-class="min-w-[84px] text-xs"
                label="品項類別"
                v-slot="props"
              >
                {{ props.row.category }}
              </b-table-column>
              <b-table-column
                header-class="text-xs"
                cell-class="max-w-[48px]"
                field="price"
                label="單價"
                numeric
                v-slot="props"
                width="48"
              >
                {{ props.row.price | priceFormat }}
              </b-table-column>
              <b-table-column
              v-if="this.inEndedOrOnGoing"
                header-class="text-xs"
                cell-class="max-w-[48px]"
                field="price"
                label="已訂數量"
                numeric
                v-slot="props"
                width="48"
              >
                {{ menuItemsLimits[props.row.index]?.current || 0 }}
              </b-table-column>
              <b-table-column
                header-class="text-xs"
                cell-class="max-w-[68px]"
                field="limit"
                label="庫存量"
                v-slot="props"
                width="68"
              >
                <b-field
                  :type="
                    v$.menuItems.$each.$response.$errors[props.index].limit?.length
                    ? 'is-danger' : ''
                  "
                  :message="v$.menuItems.$each.$response
                    .$errors[props.index].limit?.[0]?.$message"
                >
                  <b-input
                    :disabled="viewMode"
                    :min="minEntreeLimit"
                    :max="maxEntreeLimit"
                    :ref="`limitInput${props.index}`"
                    v-model="props.row.limit"
                    class="detail-card__limit-field"
                    placeholder="-"
                    type="number"
                    @input="v$.menuItems.$touch"
                    @keydown.down.native.stop.prevent="focusOnLimitInput(props.index + 1)"
                    @keydown.enter.native.stop.prevent="focusOnLimitInput(props.index + 1)"
                    @keydown.up.native.stop.prevent="focusOnLimitInput(props.index - 1)"
                  />
                </b-field>
              </b-table-column>

              <template #empty>
                <div class="text-center">無菜單品項</div>
              </template>
            </b-table>
          </div>
          <div class="auto-notify detail-card">
            <div
              class="
                flex
                flex-wrap
                items-baseline
                justify-start
                mb-4
            "
            >
              <h3 class="font-medium mr-4">
                自動發送提醒通知
              </h3>
              <span class="text-gray-500 text-sm">
                開啟通知發送功能，受邀成員將收到電子郵件通知。
              </span>
            </div>
            <div>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">公告通知</label>
                  <b-tooltip
                    label="開啟本功能，系統將針對此次排程的訂購，自動發送公告通知。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <div class="flex items-center">
                  <b-switch
                    v-model="orderInfo.notifyReserve"
                    :disabled="viewMode"
                    class="mx-2"
                    size="is-small"
                  />
                </div>
              </b-field>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">開放送單通知</label>
                  <b-tooltip
                    label="開啟本功能，系統將於此次訂購開始後，自動發送訂購通知。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <div class="is-flex is-align-items-center">
                  <b-switch
                    v-model="orderInfo.notifyStart"
                    :disabled="viewMode"
                    class="mx-2"
                    size="is-small"
                  />
                </div>
              </b-field>
              <div class="hide-in-mobile">
                <b-field horizontal>
                  <template #label>
                    <label class="mr-1">截止收單通知</label>
                    <b-tooltip
                      label="當設定通知時間，早於截止時間扣掉目前時間，則會立刻通知。"
                      multilined
                      type="is-dark"
                    >
                      <i class="icon-help-circle-outline text-gray-500" />
                    </b-tooltip>
                  </template>
                  <div class="flex flex-wrap items-center" ref="flexBox">
                    <div class="flex items-center">
                      <b-switch
                        v-model="orderInfo.notifyDeadline"
                      :disabled="viewMode"
                      class="mx-2"
                        size="is-small"
                      />
                    </div>
                    <span class="text-gray-50 mx-6" :style="{ opacity : dividerHide ? 0 : 1 }">
                      |
                    </span>
                    <div class="flex items-center flex-wrap">
                      <span class="text-gray-500 mr-2">訂購截止前</span>
                      <div class="flex items-center flex-wrap">
                        <b-field class="mr-2">
                          <b-select
                            v-model="orderInfo.shiftMin"
                            :disabled="!orderInfo.notifyDeadline || viewMode"
                            placeholder="請選擇時間"
                          >
                            <option
                              v-for="option in notifyDeadlineOptions"
                              :value="option.value"
                              :key="option.label"
                            >
                              {{ option.label }}
                            </option>
                          </b-select>
                        </b-field>
                      <span class="text-gray-500">發送通知。</span>
                      </div>
                    </div>
                  </div>
                </b-field>
              </div>
              <div class="show-in-mobile">
                <b-field horizontal>
                  <template #label>
                    <label class="mr-1">截止收單通知</label>
                    <b-tooltip
                      label="當設定通知時間，早於截止時間扣掉目前時間，則會立刻通知。"
                      multilined
                      type="is-dark"
                    >
                      <i class="icon-help-circle-outline text-gray-500" />
                    </b-tooltip>
                  </template>
                  <div class="is-flex is-align-items-center">
                    <b-switch
                      v-model="orderInfo.notifyDeadline"
                      :disabled="viewMode"
                      class="mx-2"
                      size="is-small"
                    />
                  </div>
                </b-field>
              </div>
              <div class="baseline show-in-mobile">
                <b-field horizontal>
                  <template #label>
                    <label>訂購截止前</label>
                  </template>
                  <div class="flex flex-wrap gap-2 items-center justify-end w-40">
                    <b-field class="mr-2">
                      <b-select
                        v-model="orderInfo.shiftMin"
                        :disabled="!orderInfo.notifyDeadline || viewMode"
                        expanded
                        placeholder="請選擇時間"
                      >
                        <option
                          v-for="option in notifyDeadlineOptions"
                          :value="option.value"
                          :key="option.label"
                        >
                          {{ option.label }}
                        </option>
                      </b-select>
                    </b-field>
                    <span class="text-gray-500">發送通知。</span>
                  </div>
                </b-field>
              </div>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">領取通知</label>
                  <b-tooltip
                    label="系統將於訂購人員選擇之領取時段，自動發送領取通知。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <div class="is-flex is-align-items-center">
                  <b-switch
                    v-model="orderInfo.enableSendLineNotifyInReceiveTime"
                    :disabled="viewMode"
                    class="mx-2"
                    size="is-small"
                  />
                </div>
              </b-field>
              <div class="border-b border-light-gray-500 my-7" />
              <div
                class="
                  flex
                  flex-wrap
                  items-baseline
                  justify-start
                  mb-4
              "
              >
                <h3 class="font-medium mr-4">
                  自動寄送報表
                </h3>
                <p class="text-gray-500 text-sm">
                  開啟寄送的報表，會於對應的時間點自動寄出通知至指定電子郵件信箱。
                </p>
              </div>

             <div class="send-email">
              <template v-for="sendType in sendEmailTypes">
               <b-field horizontal :key="`${sendType.label}_switch`">
                <template #label>
                  <label class="mr-1">{{ sendType.label }}</label>
                  <b-tooltip
                    :label="sendType.tooltip"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <b-switch
                  v-model="orderInfo[sendType.switch]"
                  :disabled="viewMode"
                  class="mx-2"
                  size="is-small"
                />
              </b-field>
              <b-field
                v-if="orderInfo[sendType.switch]"
                :key="`${sendType.label}_email`"
                :message="
                  hasEmailFills[sendType.email]
                    ? v$.orderInfo[sendType.email].$errors[0].$message
                    : ''
                "
                :type="{ 'is-danger': hasEmailFills[sendType.email] }"
                horizontal
              >
                <template #label>
                  <span class="required">Email</span>
                </template>
                <b-taginput
                  v-model="orderInfo[sendType.email]"
                  :before-adding="beforeAddEmail"
                  :disabled="viewMode"
                  :ref="`${sendType.email}_taginput`"
                  @input="v$.orderInfo[sendType.email] && v$.orderInfo[sendType.email].$touch()"
                >
                  <template #selected="props">
                    <b-tag
                      v-for="(tag, index) in props.tags"
                      :closable="!viewMode"
                      :key="`email-tag-${tag}-${index}`"
                      @close="closeTags(sendType, index, $event)"
                      rounded
                      type="is-primary-light"
                    >
                      {{ tag }}
                    </b-tag>
                  </template>
                </b-taginput>
              </b-field>
              </template>
             </div>
            </div>
          </div>
        </div>

        <div class="column">
          <div class="detail-card order-info">
            <h3 class="font-medium mb-4">
              訂購資訊
            </h3>
            <div>
              <b-field
                horizontal
                label="訂購名稱"
                :type="{
                  'is-danger': v$.orderInfo.name.$dirty && v$.orderInfo.name.$invalid
                }"
                :message="
                  v$.orderInfo.name.$dirty &&
                  v$.orderInfo.name.$silentErrors.length
                    ? v$.orderInfo.name.$silentErrors[0].$message
                    : ''
                ">
                <b-input
                  :disabled="viewMode"
                  name="name"
                  v-model="orderInfo.name"
                  @input="v$.orderInfo.name.$touch" />
              </b-field>
              <b-field horizontal>
                <template #label>
                  <label class="mr-1">訂購標籤</label>
                  <b-tooltip
                    label="分類標籤是方便於訂購發布後，統計報表篩選使用，僅會顯示在管理平台。"
                    multilined
                    type="is-dark"
                  >
                    <i class="icon-help-circle-outline text-gray-500" />
                  </b-tooltip>
                </template>
                <b-taginput
                  ref="createTag"
                  :disabled="viewMode"
                  name="tags"
                  v-model="orderInfo.tags"
                  placeholder="建立標籤"
                >
                  <template #selected="props">
                    <b-tag
                      v-for="(tag, index) in props.tags"
                      :key="`tag-${tag}-${index}`"
                      :closable="!viewMode"
                      rounded
                      type="is-primary-light"
                      @close="$refs.createTag.removeTag(index, $event)"
                    >
                      {{ tag }}
                    </b-tag>
                  </template>
                </b-taginput>
              </b-field>
              <b-field horizontal label="訂購備註">
                <quill-editor
                  ref="noteEditor"
                  v-model="orderInfo.note"
                  :disabled="viewMode"
                  :options="editorOptions"
                  :class="{ 'is-disabled': viewMode }"
                  class="order-note-editor"
                  @change="handleTextChange"
                />
              </b-field>
            </div>
          </div>
          <div class="detail-card">
            <h3 class="detail-card__title">開放訂購時段</h3>
            <div class="flex gap-y-2 justify-between sm:flex-col">
              <b-field
                :type="{
                  'is-danger': v$.orderInfo.startTime.$dirty && v$.orderInfo.startTime.$invalid
                }"
                :message="
                  v$.orderInfo.startTime.$dirty &&
                  v$.orderInfo.startTime.$silentErrors.length
                    ? v$.orderInfo.startTime.$silentErrors[0].$message
                    : ''
                "
                class="flex-grow mb-0"
                >
                <b-datetimepicker
                  v-model="orderInfo.startTime"
                  :datetime-formatter="datetimeFormatter"
                  :disabled="!isAllowToEdit || viewMode"
                  :max-datetime="orderTimeRange.start.max"
                  :min-datetime="orderTimeRange.start.min"
                  :mobile-native="false"
                  locale="zh-TW"
                  position="is-top-left"
                  @input="onStartTimeChange"
                />
              </b-field>
              <span class="mx-6 self-center text-gray-500">
                至
              </span>
              <b-field
                :type="{
                  'is-danger': (
                      (v$.orderInfo.endTime.$dirty && v$.orderInfo.endTime.$invalid)
                      || (isTimeBeforeNow(orderInfo.endTime) && !this.inEndedOrOnGoing)
                    )
                    && !this.viewMode
                }"
                :message="endTimeErrorMsg"
                class="flex-grow mb-0"
              >
                <b-datetimepicker
                  v-model="orderInfo.endTime"
                  locale="zh-TW"
                  position="is-top-left"
                  :disabled="viewMode"
                  :max-datetime="orderTimeRange.end.max"
                  :min-datetime="orderTimeRange.end.min"
                  :mobile-native="false"
                  :datetime-formatter="datetimeFormatter"
                  @input="onEndTimeChange"
                />
              </b-field>
            </div>
          </div>
          <div class="detail-card">
            <h3 class="font-medium mb-4">領取時段設定</h3>
            <transition-group name="period-animation" tag="div">
              <div
                v-for="(time, index) in orderReceiveTimes"
                :key="`receive-time-${index}`"
                class="period-block"
              >
                <span>
                  {{ index + 1 }}.
                </span>
                <div class="date-time-block">
                  <b-field class="flex-grow mb-0 w-full">
                    <b-datetimepicker
                      v-model="time.startTime"
                      :datetime-formatter="datetimeFormatter"
                      :disabled="disabledTime(time.startTime) || viewMode"
                      :max-datetime="orderReceiveTimeRange[index].start.max"
                      :min-datetime="orderReceiveTimeRange[index].start.min"
                      :mobile-native="false"
                      locale="zh-TW"
                      position="is-top-left"
                      @input="onReceiveStartTimeChange(index)" />
                  </b-field>
                  <span class="mx-6 text-gray-500">至</span>
                  <b-field class="flex-grow mb-0 w-full">
                    <b-datetimepicker
                      v-model="time.endTime"
                      :datetime-formatter="datetimeFormatter"
                      :disabled="disabledTime(time.endTime) || viewMode"
                      :max-datetime="orderReceiveTimeRange[index].end.max"
                      :min-datetime="orderReceiveTimeRange[index].end.min"
                      :mobile-native="false"
                      locale="zh-TW"
                      position="is-top-left"
                      @input="onReceiveEndTimeChange(index)" />
                  </b-field>
                </div>
                <a
                  v-if="
                    orderReceiveTimes.length > 1
                    && !viewMode
                    && !disabledTrash(time)
                  "
                  href="#"
                  class="period-action"
                  @click.prevent="removeReceiveTime(index)"
                >
                  <span class="icon-trash" />
                </a>
              </div>
            </transition-group>
            <div v-if="!viewMode" class="mt-4">
              <w-button color-type="secondary" type="link" @click="addReceiveTime">
                <i class="icon-plus mr-2 text-sm" />
                <span class="text-sm">添加更多時段</span>
              </w-button>
            </div>
          </div>
          <div class="detail-card">
            <h3 class="font-medium mb-6">
              公告時間
            </h3>
            <span class="text-gray-500">發起訂購後立即公告</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import OrderDetail from './OrderDetail';

export default OrderDetail;
</script>

<style lang="scss" scoped>
@import './OrderDetail.scss';
</style>
